/** Finite freshwater depressions in native humid lowlands. Closed basins have
 * no claimed outlet or current. Shared beds, islands and water levels own the
 * physical surface; renderer tiles never decide where water exists. */
import {hashSeed,rngFromString} from './rng.js';
import * as pmath from './pmath.js';
import {duneDirection} from './dunes.js';
import type {PlanetDef} from './universe.js';
import type {RiverPolyline} from './rivers.js';

type V3=readonly[number,number,number];
export interface WetlandLobe {readonly u:number;readonly v:number;readonly halfU:number;readonly halfV:number}
export interface WetlandHummock extends WetlandLobe {readonly heightM:number}
export interface WetlandChannel {readonly u0:number;readonly v0:number;readonly u1:number;readonly v1:number;readonly halfM:number}
export interface InlandWetlandDef {
 readonly id:string;readonly seed:number;readonly center:V3;readonly along:V3;readonly across:V3;
 /** Conservative tangent-plane extent, including the dry perimeter blend. */
 readonly radiusM:number;readonly waterM:number;readonly closed:true;readonly bankM:number;
 readonly lobes:readonly WetlandLobe[];readonly hummocks:readonly WetlandHummock[];
 readonly channels:readonly WetlandChannel[];
}
export interface InlandWetlandNative {
 heightM:number;temp:number;moist:number;mountain:number;plateau:number;
 coast:number;dune:number;butte:number;canyon:number;riverWs:number;
}
export interface InlandWetlandSample {heightM:number;waterM:number;mask:number;mud:number}
export const inlandWetlandSample=():InlandWetlandSample=>({heightM:0,waterM:-1e9,mask:0,mud:0});
export const inlandWetlandDirection=duneDirection;
const smooth=(x:number):number=>{const t=Math.max(0,Math.min(1,x));return t*t*(3-2*t)};
const norm=(x:number,y:number,z:number):V3=>{const r=Math.sqrt(x*x+y*y+z*z);return[x/r,y/r,z/r]};
const cross=(a:V3,b:V3):V3=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];

/** Positive inside the connected lobes; negative in the outer dry bank. */
export function inlandWetlandEdge(f:InlandWetlandDef,u:number,v:number):number {
 let edge=-Infinity;
 for(const l of f.lobes){const x=(u-l.u)/l.halfU,y=(v-l.v)/l.halfV;
  edge=Math.max(edge,(1-Math.sqrt(x*x+y*y))*Math.min(l.halfU,l.halfV));}
 return edge;
}
/** Quiet shallow water interleaves with low dry hummocks. All connected
 * channel segments share one real water elevation in this closed basin. */
export function inlandWetlandProfile(f:InlandWetlandDef,u:number,v:number,nativeM:number,out:InlandWetlandSample):InlandWetlandSample {
 out.heightM=nativeM;out.waterM=-1e9;out.mask=out.mud=0;
 if(u*u+v*v>=f.radiusM*f.radiusM)return out;
 const edge=inlandWetlandEdge(f,u,v);if(edge<=-f.bankM)return out;
 const weight=smooth((edge+f.bankM)/f.bankM);
 let bed=f.waterM-.34+1.95*(1-smooth(edge/20));
 let island=0;
 for(const h of f.hummocks){const x=(u-h.u)/h.halfU,y=(v-h.v)/h.halfV,q=x*x+y*y;if(q<1)island=Math.max(island,h.heightM*smooth(1-q));}
 bed+=island;
 let channel=0;
 for(const c of f.channels){const x=c.u1-c.u0,y=c.v1-c.v0,q=Math.max(0,Math.min(1,((u-c.u0)*x+(v-c.v0)*y)/(x*x+y*y))),dx=u-c.u0-q*x,dy=v-c.v0-q*y;
  const d2=dx*dx+dy*dy;if(d2<c.halfM*c.halfM)channel=Math.max(channel,1-smooth(Math.sqrt(d2)/c.halfM));}
 if(edge>12&&channel>0){const k=channel*smooth((edge-12)/12);bed+=(f.waterM-1.19-bed)*k;}
 out.heightM=nativeM+(bed-nativeM)*weight;out.mask=weight;
 if(edge>0)out.waterM=f.waterM;
 const depth=f.waterM-out.heightM;
 out.mud=weight*smooth((depth+1.7)/1.2)*(1-smooth((depth-.35)/1.0));
 return out;
}

export class InlandWetlandTerrain {
 readonly features:readonly InlandWetlandDef[];
 readonly enabled:boolean;
 private readonly cells=new Map<number,readonly InlandWetlandDef[]>();
 private readonly cellM:number;private readonly gridSize:number;
 private readonly scratch=inlandWetlandSample();
 constructor(private readonly def:PlanetDef,native:(x:number,y:number,z:number,out:InlandWetlandNative)=>InlandWetlandNative,rivers:readonly RiverPolyline[]){
  const R=def.radius,scale=Math.min(1,R/12000),seaM=def.seaRadius===null?-1e6:def.seaRadius-R;
  this.cellM=Math.max(80,Math.min(650,R*.06));this.gridSize=Math.ceil(2*R/this.cellM)+3;
  const features:InlandWetlandDef[]=[];this.features=features;
  this.enabled=def.atmosphere!==null&&def.hasFlora&&(def.archetype==='terran'||def.archetype==='ocean')&&def.terrain.amplitudeRel>=.001;
  if(!this.enabled){Object.freeze(features);return;}
  const scratch:InlandWetlandNative={heightM:0,temp:0,moist:0,mountain:0,plateau:0,coast:0,dune:0,butte:0,canyon:0,riverWs:-1e9};
  const count=1536,phase=rngFromString(`${def.seed}:inland-wetlands:sites:v1`)()*pmath.TWO_PI;
  const eligible=(s:InlandWetlandNative)=>s.temp>-.22&&s.temp<.68&&s.moist>.02&&s.heightM>seaM+35&&s.mountain<.12&&s.plateau<.06&&s.coast<.001&&s.dune<.001&&s.butte<.001&&s.canyon<.001&&s.riverWs<-1e8;
  for(let candidate=0;candidate<count&&features.length<12;candidate++){
   const i=(candidate*641)%count,y=1-2*(i+.5)/count,a=phase+i*2.399963229728653,r=Math.sqrt(1-y*y),center:V3=[r*pmath.cos(a),y,r*pmath.sin(a)];
   const first=native(...center,scratch);if(!eligible(first))continue;
   const centerM=first.heightM,random=rngFromString(`${def.seed}:inland-wetland:${i}`),seed=hashSeed(`${def.seed}:inland-wetland:${i}`);
   const e=norm(center[2],0,-center[0]),n=cross(center,e),angle=random()*pmath.TWO_PI,c=pmath.cos(angle),s=pmath.sin(angle);
   const along:V3=[e[0]*c+n[0]*s,e[1]*c+n[1]*s,e[2]*c+n[2]*s],across=cross(along,center),frame={center,along,across};
   const length=(150+random()*90)*scale,width=(75+random()*45)*scale,bankM=Math.max(12,32*scale);
   const lobes:WetlandLobe[]=[{u:-length*.34,v:-width*.12,halfU:length*.76,halfV:width},{u:length*.35,v:width*.22,halfU:length*.69,halfV:width*.88},{u:length*.1,v:-width*.50,halfU:length*.48,halfV:width*.73}];
   const radiusM=Math.max(...lobes.map(l=>Math.sqrt(l.u*l.u+l.v*l.v)+Math.max(l.halfU,l.halfV)))+bankM+2;
   if(features.some(f=>{const d=(radiusM+f.radiusM+200*scale)/R;return center[0]*f.center[0]+center[1]*f.center[1]+center[2]*f.center[2]>1-d*d*.5;}))continue;
   // Entire existing river corridors remain outside the new depression. A
   // segment's chord distance is conservative at these bounded metre scales.
   let blocked=false;
   for(const route of rivers){for(let j=1;j<route.points.length;j++){const p=route.points[j-1],q=route.points[j],ax=(p.x-center[0])*R,ay=(p.y-center[1])*R,az=(p.z-center[2])*R,bx=(q.x-p.x)*R,by=(q.y-p.y)*R,bz=(q.z-p.z)*R;
    const den=bx*bx+by*by+bz*bz,t=den?Math.max(0,Math.min(1,-(ax*bx+ay*by+az*bz)/den)):0,dx=ax+t*bx,dy=ay+t*by,dz=az+t*bz,gap=radiusM+160+Math.max(p.half,q.half);
    if(dx*dx+dy*dy+dz*dz<gap*gap){blocked=true;break;}}
    if(blocked)break;}if(blocked)continue;
   let low=centerM,high=centerM;
   // Reject cliffs, specialist/coastal features and steep native gradients
   // throughout the conservative bound, not just at the basin center.
   for(let u=-4;u<=4&&!blocked;u++)for(let v=-4;v<=4;v++){
    if(u*u+v*v>18)continue;const d=inlandWetlandDirection(frame,R,u*radiusM/4,v*radiusM/4),z=native(...d,scratch);
    if(!eligible(z)){blocked=true;break;}low=Math.min(low,z.heightM);high=Math.max(high,z.heightM);
   }
   if(blocked||high-low>24||centerM-low>8||high-centerM>19)continue;
   const waterM=Math.max(low+1.2,centerM-1.4);
   const channels:WetlandChannel[]=[{u0:-length*.75,v0:-width*.10,u1:0,v1:-width*.22,halfM:9+random()*7},{u0:0,v0:-width*.22,u1:length*.72,v1:width*.25,halfM:10+random()*9},{u0:-length*.1,v0:-width*.2,u1:length*.13,v1:-width*.79,halfM:8+random()*7}];
   const hummocks:WetlandHummock[]=[];
   for(let j=0;j<22;j++)hummocks.push(Object.freeze({u:(random()-.5)*length*1.75,v:(random()-.5)*width*1.7,halfU:(13+random()*24)*scale,halfV:(10+random()*20)*scale,heightM:.85+random()*1.45}));
   features.push(Object.freeze({id:`${def.id}:inland-wetland:${i}`,seed,center:Object.freeze(center),along:Object.freeze(along),across:Object.freeze(across),radiusM,waterM,closed:true,bankM,lobes:Object.freeze(lobes.map(x=>Object.freeze(x))),hummocks:Object.freeze(hummocks),channels:Object.freeze(channels.map(x=>Object.freeze(x)))}));
  }
  Object.freeze(features);
  const mutable=new Map<number,InlandWetlandDef[]>();
  for(const f of features){const bound=f.radiusM+R*(1-R/Math.sqrt(R*R+f.radiusM*f.radiusM));
   const lo=f.center.map(x=>Math.max(0,Math.floor((x*R-bound+R)/this.cellM))),hi=f.center.map(x=>Math.min(this.gridSize-1,Math.floor((x*R+bound+R)/this.cellM)));
   for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){const key=this.key(x,y,z);let list=mutable.get(key);if(!list)mutable.set(key,list=[]);list.push(f);}}
  for(const[key,list]of mutable)this.cells.set(key,Object.freeze(list));
 }
 private key(x:number,y:number,z:number):number{return(x*this.gridSize+y)*this.gridSize+z}
 sample(dx:number,dy:number,dz:number,heightM:number,out:InlandWetlandSample):InlandWetlandSample{
  out.heightM=heightM;out.waterM=-1e9;out.mask=out.mud=0;if(!this.enabled)return out;
  const R=this.def.radius,c=this.cellM,list=this.cells.get(this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)));if(!list)return out;
  for(const f of list){const face=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(face<=0)continue;const q=R/face,u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*q,v=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*q;
   const s=inlandWetlandProfile(f,u,v,heightM,this.scratch);if(s.mask>out.mask){out.heightM=s.heightM;out.waterM=s.waterM;out.mask=s.mask;out.mud=s.mud;}}
  return out;
 }
}
